import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import * as SelectPrimitive from "@radix-ui/react-select";
import { SudoButton } from "../SudoButton";
import { optionVariants } from "./shared";

/**
 * @typedef {'default' | 'success' | 'danger'} IconStatus
 * @typedef {{ value: string, label: string, icon?: { label: string, src: string, status: IconStatus }, async?: { state: string, loadingPercentage: number } }} Option
 * @typedef {'left' | 'center'} OptionAlignment
 */

const SelectBody =
  /**
   * @param {Object} props
   * @param {Array<Option>} props.matches
   * @param {React.Ref<HTMLInputElement>} props.inputRef
   * @param {string} [props.maxHeight="300px"]
   * @param {string} [props.selectedValue]
   * @param {string} [props.searchLabel=""]
   * @param {boolean} [props.canAddCustom=false]
   * @param {string} [props.customValue=""]
   * @param {(value: string) => void} [props.debounceSetValue]
   * @param {string} [props.menuLabel=""]
   * @param {OptionAlignment} [props.optionAlignment]
   * @param {React.RefObject<HTMLElement>} props.collisionBoundary
   * @param {React.RefObject<HTMLElement>} [props.portalContainer]
   * @param {React.RefObject<HTMLElement>} [props.triggerRef]
   * @param {boolean} [props.open=false]
   * @param {string} [props.className=""]
   * @param {React.ReactNode} [props.children]
   * @param {(value: string) => void} [props.onOptionClick]
   * @param {(open: boolean) => void} [props.onOpenChange]
   * @param {boolean} [props.forceFixedPosition]
   * @param {'default' | 'small'} [props.selectSize]
   * @returns {JSX.Element}
   */
  ({
    matches,
    inputRef,
    maxHeight = "300px",
    selectedValue,
    canAddCustom = false,
    searchLabel = "",
    customValue = "",
    debounceSetValue,
    menuLabel = "",
    optionAlignment,
    collisionBoundary,
    portalContainer,
    open = false,
    className = "",
    // New: positioning controls
    side = "bottom",
    align = "center",
    sideOffset = 0,
    alignOffset = 0,
    triggerRef,
    onOptionClick,
    onOpenChange,
    forceFixedPosition = true,
    selectSize = 'default',
    children,
  }) => {
    const [value, setValue] = useState(customValue);
    const [dynamicMaxHeight, setDynamicMaxHeight] = useState(maxHeight);
    const [isScrollable, setIsScrollable] = useState(false);
    const [dynamicMenuWidthContent, setDynamicMenuWidthContent] = useState(null);
    // triggerRef is optionally passed from parent; when omitted we won't rely on it
    const viewportRef = useRef(null);
    const contentRef = useRef(null);
    const contentInnerRef = useRef(null);

    const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

    useEffect(() => {
      setValue("");
    }, [selectedValue]);

    // The filtering will shift focus to the menu, so we need
    // to set the focus back to the input so that the user can
    // continue typing if they want to
    useEffect(() => {
      if (inputRef.current && customValue) {
        setTimeout(() => {
          inputRef.current.focus();
        }, 0);
      }
    }, [matches, customValue, inputRef]);

    // Update dynamic maxHeight when menu opens
    useEffect(() => {
      if (!open) return;
      const minHeightPx = 160;
      const padding = 12;
      let available = window.innerHeight / 2;
      try {
        if (triggerRef && triggerRef.current && typeof triggerRef.current.getBoundingClientRect === "function") {
          const rect = triggerRef.current.getBoundingClientRect();
          setMenuPos({ top: rect.top, left: rect.left, width: rect.width });
          if (side === "top") {
            available = Math.max(rect.top - padding, minHeightPx);
          } else {
            available = Math.max(window.innerHeight - rect.bottom - padding, minHeightPx);
          }
        } else {
          available = Math.max(available, minHeightPx);
        }
      } catch (e) {
        available = Math.max(available, minHeightPx);
      }
      setDynamicMaxHeight(`${Math.floor(available)}px`);
    }, [open, side, triggerRef]);

    useEffect(() => {
      if (!viewportRef.current || !contentRef.current) return;

      const checkScrollable = () => {
        if (!viewportRef.current || !contentRef.current) return;
        const viewportHeight = viewportRef.current.clientHeight;
        const contentHeight = contentRef.current.scrollHeight;
        setIsScrollable(contentHeight > viewportHeight);
      };

      const resizeObserver = new ResizeObserver(checkScrollable);
      resizeObserver.observe(contentRef.current);
      resizeObserver.observe(viewportRef.current);

      // Initial check
      checkScrollable();

      return () => {
        resizeObserver.disconnect();
      };
    }, [matches, children, customValue]); // Re-run when content changes

    // Measure content width to size menu to content
    useEffect(() => {
      if (!open) {
        setDynamicMenuWidthContent(null);
        return;
      }
      const measure = () => {
        try {
          const container = contentInnerRef.current;
          if (!container) return;
          const rows = container.querySelectorAll('[data-option-row="true"]');
          let max = 0;
          let hasIcon = false;
          rows.forEach((node) => {
            const w = node.scrollWidth || node.offsetWidth || 0;
            if (w > max) max = w;
            // Check if parent button has an icon (SVG sibling)
            const button = node.closest('button');
            if (button && button.querySelector('svg')) {
              hasIcon = true;
            }
          });
          // Base padding for container borders and menu padding
          let padding = 24;
          // Add extra space for icon + its padding when icons are present
          if (hasIcon) {
            padding += 40; // icon (24px) + left padding (8px) + gap (8px)
          }
          if (max > 0) setDynamicMenuWidthContent(Math.ceil(max + padding));
        } catch (_) {}
      };
      const raf = requestAnimationFrame(measure);
      window.addEventListener('resize', measure);
      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener('resize', measure);
      };
    }, [open, matches]);

    const selectedStateStyles = (option) => {
      const isSelected = option.value === selectedValue;
      let styles = "";
      if (option.icon && option.icon.status) {
        styles += optionVariants({ variant: option.icon.status, selected: isSelected });
        styles += ` active:before:ml-[3px] pr-4`;
      } else if (canAddCustom) {
        styles += optionVariants({ selected: isSelected });
        styles += " justify-start pr-4";
      } else {
        styles += optionVariants({ selected: isSelected });
      }

      // Ensure visible hover-like background for selected rows in all paths (incl. fixed fallback)
      if (isSelected) {
        styles += " before:bg-[var(--menu-color-hover)] before:rounded-[10px] rounded-[10px]";
      }

      return styles;
    };

    // Removed debug logging

    // Fixed-position fallback when showing above to avoid clipping by body overflow
    if (open && side === "top" && forceFixedPosition && triggerRef && triggerRef.current) {
      return (
        <>
          {(() => {
            const widthPx = dynamicMenuWidthContent || menuPos.width;
            const style = {
              position: "fixed",
              top: `${menuPos.top - (sideOffset || 0)}px`,
              left: `${menuPos.left}px`,
              transform: "translateY(-100%)",
              width: `${widthPx}px`,
              zIndex: 2147483647,
            };
            return (
              typeof document !== "undefined" &&
              typeof window !== "undefined" &&
              createPortal(
                <div style={style} className={`bg-app-menu-color shadow-card border border-app-menu-color-hover p-1 rounded-xl overflow-hidden`} onMouseDown={(e) => e.stopPropagation()}>
                  <ScrollArea.Root
                    className="w-full"
                    type="auto"
                    style={{ maxHeight: dynamicMaxHeight }}
                  >
                    <ScrollArea.Viewport
                      className="bg-app-menu-color w-full rounded-b-xl"
                      style={{ maxHeight: dynamicMaxHeight }}
                    >
                      <div ref={contentInnerRef} className={isScrollable ? "pr-3" : ""}>
                        {matches.length > 0 &&
                          matches.map((option) => (
                            <div key={option.value} className={`outline-none px-0 mb-1 ${isScrollable ? "first:mt-1 last:mb-1" : "first:mt-0 last:mb-0"}`}>
                              <SudoButton
                                variant={"tertiaryGrey"}
                                className={`w-full first:mt-0 ${selectedStateStyles(option)} last:mb-0 ${
                                  option.icon
                                    ? optionAlignment === "left"
                                      ? "pl-2"
                                      : "pl-2"
                                    : "pl-0"
                                } ${optionAlignment === "left" ? "justify-start" : "justify-center"}`}
                                iconSize={24}
                                leftIcon={option.icon?.src}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (onOptionClick) onOptionClick(option.value);
                                  if (onOpenChange) onOpenChange(false);
                                }}
                              >
                                {!option.subMenu ? (
                                  <div data-option-row="true" className={`${option.icon ? "" : "px-3"} flex items-center gap-2`}>
                                    <span className="truncate">{option.label}</span>
                                    {option.inlineSubLabel && (
                                      <span className="text-app-grey-1 font-inter font-medium text-sm whitespace-nowrap">{option.inlineSubLabel}</span>
                                    )}
                                  </div>
                                ) : (
                                  <option.subMenu />
                                )}
                              </SudoButton>
                            </div>
                          ))}
                        {children}
                      </div>
                    </ScrollArea.Viewport>
                    <ScrollArea.Scrollbar
                      className="w-2 bg-gray-200 transition-colors duration-200 rounded-xl h-[97%]"
                      orientation="vertical"
                    >
                      <ScrollArea.Thumb className="bg-gray-300 rounded-xl" />
                    </ScrollArea.Scrollbar>
                  </ScrollArea.Root>
                </div>,
                document.body
              )
            );
          })()}
          {/* Close on outside click */}
          {createPortal(
            <div
              onClick={() => onOpenChange && onOpenChange(false)}
              style={{ position: "fixed", inset: 0, zIndex: 2147483646 }}
            />,
            document.body
          )}
        </>
      );
    }

    return (
      <SelectPrimitive.Portal container={portalContainer?.current}>
        <SelectPrimitive.Content
          avoidCollisions={false}
          position="popper"
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          collisionBoundary={collisionBoundary.current}
          collisionPadding={{ top: 8, bottom: 8 }}
          className={`${side === "top" ? "origin-bottom" : "origin-top"} [&[data-state='open']]:animate-flyout bg-app-menu-color shadow-card border border-app-menu-color-hover ${side === "top" ? "mb-2" : "mt-2"} p-1 rounded-xl overflow-hidden z-[2147483647] ${className}`}
          style={dynamicMenuWidthContent ? { width: `${dynamicMenuWidthContent}px`, maxHeight: dynamicMaxHeight } : { minWidth: "var(--radix-select-trigger-width)", maxHeight: dynamicMaxHeight }}
          ref={contentRef}
          // @ts-ignore - triggerRef is optional, used for dynamic sizing when portaled
          data-trigger-bound="true"
        >
          {menuLabel && <p className="font-inter font-medium text-app-grey-0 text-base px-2 pb-2 pt-1">{menuLabel}</p>}
          <ScrollArea.Root
            className="w-full"
            type="auto"
            style={{
              maxHeight: dynamicMaxHeight,
            }}
          >
            <ScrollArea.Viewport
              ref={viewportRef}
              className="bg-app-menu-color w-full rounded-b-xl"
              style={{
                maxHeight: dynamicMaxHeight,
              }}
            >
              <div
                ref={contentInnerRef}
                className={isScrollable ? "pr-3" : ""}
              >
                {canAddCustom && (
                  <input
                    ref={inputRef}
                    type="text"
                    value={value || ""}
                    onChange={(event) => {
                      setValue(event.target.value);
                      debounceSetValue(event.target.value);
                    }}
                    placeholder={searchLabel}
                    className="combobox border-0 h-[40px] font-inter text-base font-medium text-app-grey-0 bg-app-menu-color-hover rounded-[10px] px-3 mb-1 outline-none data-[disabled]:pointer-events-none data-[highlighted]:rounded-[10px]"
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        event.stopPropagation();
                      }
                    }}
                  />
                )}
                {matches.length > 0 &&
                  matches.map((option) => {
                    return (
                      <SelectPrimitive.Item
                        key={option.value}
                        value={option.value}
                        className={`outline-none px-0 mb-1 ${isScrollable ? "first:mt-1 last:mb-1" : "first:mt-0 last:mb-0"} group`}
                        aria-selected={option.value === selectedValue}
                        onSelect={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (onOptionClick) onOptionClick(option.value);
                        }}
                      >
                        <SudoButton
                          variant={"tertiaryGrey"}
                          className={`w-full first:mt-0 ${selectedStateStyles(option)} last:mb-0 ${
                            option.icon
                              ? optionAlignment === "left"
                                ? "pl-2 group-focus:before:ml-[-7px] hover:before:ml-[-7px] active:before:ml-[-7px]"
                                : "pl-2 group-focus:before:ml-[7px] hover:before:ml-[7px] active:before:ml-[7px]"
                              : "pl-0"
                          } ${optionAlignment === "left" ? "justify-start" : "justify-center"}`}
                          iconSize={24}
                          leftIcon={option.icon?.src}
                        >
                          <SelectPrimitive.ItemText>
                            {!option.subMenu ? (
                              <div data-option-row="true" className={`${option.icon ? "" : "px-3"} flex items-center gap-2`}>
                                <span className="truncate">{option.label}</span>
                                {option.inlineSubLabel && (
                                  <span className="text-app-grey-1 font-inter font-medium text-sm whitespace-nowrap">{option.inlineSubLabel}</span>
                                )}
                              </div>
                            ) : (
                              <option.subMenu />
                            )}
                          </SelectPrimitive.ItemText>
                        </SudoButton>
                      </SelectPrimitive.Item>
                    );
                  })}
                {children}
              </div>
            </ScrollArea.Viewport>
            <ScrollArea.Scrollbar
              className="w-2 bg-gray-200 transition-colors duration-200 rounded-xl h-[97%]"
              orientation="vertical"
            >
              <ScrollArea.Thumb className="bg-gray-300 rounded-xl" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    );
  };

SelectBody.displayName = "SelectBody";

export default SelectBody;
